/**
 * Aggregates all derived view-state for the CoverageDetails page:
 * tab grouping, ordered representative coverages, the active validate-EV
 * coverage_result, service type label map, submit-button label, happy-modal
 * gating, and the primary insurance active flag.
 */
import { useMemo } from "react";
import type { CoverageInformation } from "../../../../data-model/coverageInformation";
import {
  CoverageResult,
  SingleCoverage,
  Status,
} from "../../../../data-model/medicalPaOrdersModel";
import { EVWriteBackStatus } from "../../../../enums/evBvWriteBackStatus";
import { buildRequestedServiceTypeLabelMap } from "../../helpers/buildRequestedServiceTypeLabelMap";
import { getGroupedCoverageTabsForDisplay } from "../../helpers/coverageDisplayOrder";
import { getPlanActivityStatus } from "../../helpers/coverageStatusUtils";

interface UseCoverageDerivedStateArgs {
  coverages: SingleCoverage[] | undefined;
  coverage_results: CoverageResult[] | undefined;
  coverageInformation: CoverageInformation | null;
  status: Status | undefined;
  activeTabIndex: number;
}

interface UseCoverageDerivedStateResult {
  groupedCoverageTabsForDisplay: ReturnType<
    typeof getGroupedCoverageTabsForDisplay
  >;
  orderedCoverages: SingleCoverage[];
  activeCoverageResultForValidateEv: CoverageResult | undefined;
  serviceTypeCodeLabelMap: ReadonlyMap<string, string>;
  submitButtonText: string;
  shouldOpenHappyModal: boolean;
  isPrimaryInsuranceInActive: boolean;
}

/** Bundles the derived memoized state used across the CoverageDetails tree. */
export const useCoverageDerivedState = ({
  coverages,
  coverage_results,
  coverageInformation,
  status,
  activeTabIndex,
}: UseCoverageDerivedStateArgs): UseCoverageDerivedStateResult => {
  const groupedCoverageTabsForDisplay = useMemo(
    () =>
      getGroupedCoverageTabsForDisplay(coverages ?? [], coverage_results ?? []),
    [coverages, coverage_results],
  );

  const orderedCoverages = useMemo(
    () =>
      groupedCoverageTabsForDisplay.map((tab) => tab.representativeCoverage),
    [groupedCoverageTabsForDisplay],
  );

  // EV validate modal must use the same `coverage_results` row as the active
  // tab; raw API array order can differ from tab order (primary/secondary).
  const activeCoverageResultForValidateEv = useMemo(
    () =>
      groupedCoverageTabsForDisplay[activeTabIndex]
        ?.representativeCoverageResult,
    [groupedCoverageTabsForDisplay, activeTabIndex],
  );

  const serviceTypeCodeLabelMap = useMemo(
    () => buildRequestedServiceTypeLabelMap(coverage_results ?? []),
    [coverage_results],
  );

  const submitButtonText = useMemo(() => {
    const primaryPlanStatus =
      coverageInformation?.[0]?.singleCoverageInformation.planInformation
        .planStatus;
    if (getPlanActivityStatus(primaryPlanStatus) === "inactive") {
      return "Mark as Completed";
    }
    return "Continue";
  }, [coverageInformation]);

  const shouldOpenHappyModal = useMemo(() => {
    const evWriteBackStatusForHappyModal = [
      EVWriteBackStatus.SUCCESS,
      EVWriteBackStatus.IN_PROGRESS,
      EVWriteBackStatus.QUEUED,
    ];

    const evStatus = status?.ev_write_back_status;

    if (!evStatus) return false;

    return evWriteBackStatusForHappyModal.includes(
      evStatus as EVWriteBackStatus,
    );
  }, [status?.ev_write_back_status]);

  const isPrimaryInsuranceInActive = useMemo(() => {
    const primaryPlanStatus =
      coverageInformation?.[0]?.singleCoverageInformation.planInformation
        .planStatus;
    return getPlanActivityStatus(primaryPlanStatus) === "inactive";
  }, [coverageInformation]);

  return {
    groupedCoverageTabsForDisplay,
    orderedCoverages,
    activeCoverageResultForValidateEv,
    serviceTypeCodeLabelMap,
    submitButtonText,
    shouldOpenHappyModal,
    isPrimaryInsuranceInActive,
  };
};
